import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { ToastService } from '../../../../../shared/services/toast.service';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketCustomField, TicketCustomFieldType } from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-custom-fields-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './custom-fields-form.component.html'
})
export class TicketsCustomFieldsFormComponent implements OnDestroy {
  @Output() saved = new EventEmitter<TicketCustomField>();
  @Output() cancelled = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private configApi = inject(TicketsConfigApiService);
  private toast = inject(ToastService);

  isEditing = false;
  itemId: number | null = null;
  saving = false;
  error: string | null = null;
  private originalName = '';

  readonly fieldTypes: TicketCustomFieldType[] = [
    'TEXT',
    'NUMBER',
    'DATE',
    'SELECT',
    'MULTISELECT',
    'CHECKBOX'
  ];

  form: FormGroup = this.fb.group({
    code: ['', [Validators.required, Validators.maxLength(80), Validators.pattern(/^[a-z0-9_]+$/)]],
    label: ['', [Validators.required, Validators.maxLength(150)]],
    fieldType: ['TEXT' as TicketCustomFieldType, [Validators.required]],
    optionsCsv: [''], // CSV para SELECT/MULTISELECT
    isRequired: [false],
    sortOrder: [0],
    sectionName: [''],
    isActive: [true]
  });

  private readonly destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reset(): void {
    this.isEditing = false;
    this.itemId = null;
    this.error = null;
    this.saving = false;
    this.form.reset({
      code: '',
      label: '',
      fieldType: 'TEXT',
      optionsCsv: '',
      isRequired: false,
      sortOrder: 0,
      sectionName: '',
      isActive: true
    });
    this.form.get('code')?.enable();
  }

  loadField(id: number): void {
    this.isEditing = true;
    this.itemId = id;
    this.configApi.getCustomFieldById(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        const optionsCsv = Array.isArray(item.options) ? (item.options as string[]).join(', ') : '';
        this.form.patchValue({
          code: item.code,
          label: item.label,
          fieldType: item.fieldType,
          optionsCsv,
          isRequired: item.isRequired,
          sortOrder: item.sortOrder ?? 0,
          sectionName: item.sectionName ?? '',
          isActive: item.isActive
        });
        this.originalName = item.label;
        // code es identificador único; bloquear en edit para evitar mover el field a otro key
        this.form.get('code')?.disable();
      },
      error: (err) => { this.error = err.error?.message || 'Error al cargar el campo'; }
    });
  }

  needsOptions(): boolean {
    const t = this.form.get('fieldType')?.value as TicketCustomFieldType;
    return t === 'SELECT' || t === 'MULTISELECT';
  }

  hasError(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  guardar(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    this.error = null;
    const raw = this.form.getRawValue();

    let options: unknown;
    if (this.needsOptions() && raw.optionsCsv) {
      options = (raw.optionsCsv as string)
        .split(',').map((s: string) => s.trim()).filter(Boolean);
    } else if (this.needsOptions()) {
      this.saving = false;
      this.error = 'Debes definir al menos una opción';
      return;
    }

    const req = {
      code: raw.code,
      label: raw.label,
      fieldType: raw.fieldType as TicketCustomFieldType,
      options,
      isRequired: !!raw.isRequired,
      sortOrder: Number(raw.sortOrder ?? 0),
      sectionName: raw.sectionName || undefined,
      isActive: !!raw.isActive
    };

    const obs = this.isEditing && this.itemId != null
      ? this.configApi.updateCustomField(this.itemId, req)
      : this.configApi.createCustomField(req);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) this.toast.updated('Campo personalizado',
          this.originalName !== (raw.label?.trim() || '')
            ? `Se actualizó "${this.originalName}" → "${raw.label?.trim() || ''}".`
            : `Se actualizó "${raw.label?.trim() || ''}" correctamente.`);
        else this.toast.created('Campo personalizado', `Se creó "${raw.label?.trim() || ''}" correctamente.`);
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar el campo';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  cancelar(): void { this.cancelled.emit(); }
}
